// --- START EDITS: Robust fetch + validation; env-driven axios base prevents same-origin HTML responses ---
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/lib/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/ui/popover";
import { Button } from "@/lib/components/ui/button";
import { ChevronDown } from "lucide-react";
import axiosInstance from "@/auth/axiosWithInterceptor";
import { validateFilamentOptions } from "@/lib/validators/inventory";

export interface FilamentOption {
  inventory_id: number;
  material: string;
  color: string;
  hex_code: string;
  price_per_gram: number;
  diameter: number;
  density: number;
}

interface FilamentPickerProps {
  selectedFilamentId: number | null;
  setSelectedFilamentId: (id: number) => void;
}

// Group and sort filaments by material and color
function groupFilamentsByMaterial(
  filaments: FilamentOption[]
): [string, FilamentOption[]][] {
  const groups = new Map<string, FilamentOption[]>();

  filaments.forEach((f) => {
    if (!groups.has(f.material)) {
      groups.set(f.material, []);
    }
    groups.get(f.material)!.push(f);
  });

  // Sort colors within each material alphabetically
  groups.forEach((colors) => {
    colors.sort((a, b) => a.color.localeCompare(b.color));
  });

  // Convert to array and sort materials alphabetically
  return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}

const FilamentPicker: React.FC<FilamentPickerProps> = ({
  selectedFilamentId,
  setSelectedFilamentId,
}) => {
  const [filaments, setFilaments] = useState<FilamentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined
  );
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Group filaments by material, sort materials alphabetically, sort colors alphabetically
  const groupedFilaments = useMemo(
    () => groupFilamentsByMaterial(filaments),
    [filaments]
  );

  useEffect(() => {
    async function fetchOptions() {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get("/inventory/filament-options", {
          // Avoid intermediary caches returning 304 without a body
          headers: { "Cache-Control": "no-cache" },
          withCredentials: true,
        });

        // Validate payload strictly - throws on invalid shape
        const data = validateFilamentOptions(res.data);
        setFilaments(data);

        // Default selection: PLA - Black if exists, otherwise first material's first color
        if (data.length > 0 && !selectedFilamentId) {
          const plaBlack = data.find(
            (f) => f.material === "PLA" && f.color === "Black"
          );
          if (plaBlack) {
            setSelectedFilamentId(plaBlack.inventory_id);
          } else {
            const grouped = groupFilamentsByMaterial(data);
            if (grouped.length > 0) {
              const [, firstColors] = grouped[0];
              setSelectedFilamentId(firstColors[0].inventory_id);
            }
          }
        }
      } catch (e: unknown) {
        // Friendly error; keep previous valid state to avoid crashes
        setError("Failed to load active filaments.");
        // Optionally log details in development:
        if (import.meta.env.DEV) {
          console.error("filament-options fetch failed", e);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchOptions();
    // Only on mount / when selection handler changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelectedFilamentId]);

  // Measure trigger width when it becomes available
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [filaments]);

  const selectedFilament = filaments.find(
    (f) => f.inventory_id === selectedFilamentId
  );

  const handleSelect = (inventoryId: number) => {
    setSelectedFilamentId(inventoryId);
    setOpen(false);
  };

  return (
    <div className="space-y-2.5">
      <label className="text-sm font-medium text-foreground block">
        Filament
      </label>
      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : error ? (
        <div className="text-red-500 text-sm">{error}</div>
      ) : filaments.length === 0 ? (
        <div className="text-muted-foreground text-sm">No active filaments</div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
            >
              {selectedFilament ? (
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block size-4 rounded-full border border-border shrink-0"
                    style={{ backgroundColor: selectedFilament.hex_code }}
                    aria-label={selectedFilament.color}
                  />
                  <span className="truncate">
                    {selectedFilament.material} — {selectedFilament.color}
                  </span>
                </div>
              ) : (
                <span>Select filament...</span>
              )}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0" align="start" style={{ width: triggerWidth }}>
            <div className="max-h-[400px] overflow-y-auto">
              <Accordion type="single" collapsible>
                {groupedFilaments.map(([material, colors]) => (
                  <AccordionItem key={material} value={material}>
                    <AccordionTrigger className="px-4">
                      <span className="font-medium">{material}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <div className="space-y-1">
                        {colors.map((f) => (
                          <button
                            key={f.inventory_id}
                            type="button"
                            onClick={() => handleSelect(f.inventory_id)}
                            className={`w-full flex items-center gap-2 p-2 rounded-md text-sm transition-colors ${
                              f.inventory_id === selectedFilamentId
                                ? "bg-primary text-primary-foreground"
                                : "hover:bg-muted"
                            }`}
                          >
                            <span
                              className="inline-block size-4 rounded-full border border-border shrink-0"
                              style={{ backgroundColor: f.hex_code }}
                              aria-label={f.color}
                            />
                            <span>{f.color}</span>
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default FilamentPicker;
// --- END EDITS: Robust fetch + validation; env-driven axios base prevents same-origin HTML responses ---